(() => {
  // Every modular Section references this shared asset so that it still works
  // when the Hero is removed or disabled. Shopify can therefore execute the
  // same asset tag more than once; only the first execution installs the page
  // controller and theme-editor listeners.
  if (window.__cloudXModularControllerLoaded) return;
  window.__cloudXModularControllerLoaded = true;

  const controllers = new Map();
  const defaultModeData = {
    carry: ['Everyday carry', '≈680G*', 'Made to move through your day.', 'A complete keyboard system designed around everyday carry.'],
    protect: ['Protection', 'DETACHABLE', 'Protection that stays with your iPad.', 'Remove the keyboard while the protective shell remains in place.'],
    trackpad: ['Glass trackpad', 'PHYSICAL CLICK', 'Click with confidence.', 'A glass surface and physical feedback for more precise navigation.'],
    angles: ['Flexible viewing', '40°–135°', 'One setup. More working positions.', 'Adjust the view for typing, calls, reading and entertainment.'],
    typing: ['Keyboard experience', '1.2MM', 'Built for real work.', 'Comfortable scissor-switch typing for documents, notes and planning.']
  };

  const selectorEscape = (value) => {
    if (window.CSS?.escape) return window.CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  };

  const normalizedOptionName = (value) => String(value || '').trim().toLowerCase();
  const normalizedModelNumber = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const modelNumbersForEntry = (entry) => (Array.isArray(entry?.model_number) ? entry.model_number : String(entry?.model_number || '').split(/[,;/|\s]+/))
    .map(normalizedModelNumber)
    .filter(Boolean);
  const modelSearchTermsForEntry = (entry) => [
    ...modelNumbersForEntry(entry),
    ...(Array.isArray(entry?.aliases) ? entry.aliases : String(entry?.aliases || '').split(/[,;/|]+/)).map(normalizedModelNumber)
  ].filter(Boolean);
  const normalizedScreenSize = (value) => {
    const source = String(value || '').trim();
    if (!source) return '';
    const match = source.match(/(\d{1,2}(?:\.\d)?)\s*(?:-\s*)?(?:inch(?:es)?|in\b|[″”"])/i);
    if (match) return `${match[1]}-inch`;
    const knownSize = source.match(/\b(7\.9|8\.3|10\.2|10\.5|10\.9|11|12\.9|13)\b/);
    return knownSize ? `${knownSize[1]}-inch` : '';
  };
  const screenSizeForEntry = (entry) => normalizedScreenSize(entry?.screen_size)
    || normalizedScreenSize(`${entry?.display_name || ''} ${entry?.option_value || ''}`)
    || 'Other sizes';
  const optionValuesForEntry = (entry) => [entry?.option_value, ...(Array.isArray(entry?.legacy_option_values) ? entry.legacy_option_values : [])]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const optionValueForEntry = (entry, productValues) => {
    const explicitValue = optionValuesForEntry(entry).find((value) => productValues.includes(value));
    if (explicitValue) return explicitValue;
    const entrySize = normalizedScreenSize(entry?.screen_size || entry?.display_name);
    if (!entrySize) return '';
    const sizeMatches = productValues.filter((value) => normalizedScreenSize(value) === entrySize);
    return sizeMatches.length === 1 ? sizeMatches[0] : '';
  };
  const compatibilityEntryKey = (entry) => String(
    entry?.model_key
      || modelNumbersForEntry(entry)[0]
      || entry?.display_name
      || `${entry?.family || ''}-${entry?.screen_size || ''}-${entry?.generation || ''}`
  ).trim();

  function installResponsiveImages(root) {
    root.querySelectorAll('[data-cloud-x-image-template]').forEach((template) => {
      const blockId = selectorEscape(template.dataset.cloudXBlockId || '');
      const assetCode = selectorEscape(template.dataset.cloudXAssetCode || '');
      const target = root.querySelector(`[data-cloud-x-block="${blockId}"] [data-asset="${assetCode}"]`);
      if (!target || target.classList.contains('has-video')) return;
      const media = template.content.cloneNode(true);
      [...media.children].forEach((node) => { node.dataset.cloudXInstalledMedia = 'true'; });
      target.querySelectorAll(':scope > [data-cloud-x-installed-media="true"]').forEach((node) => node.remove());
      if (root.dataset.cloudXDesignMode === 'true') target.append(media);
      else target.replaceChildren(media);
      target.classList.add('has-cloud-x-responsive-media');
      if (template.dataset.cloudXHideFallback === 'true') target.classList.add('hide-cloud-x-fallback-art');
    });

    if (root.dataset.cloudXDesignMode !== 'true') {
      root.querySelectorAll('.asset-frame[data-asset]:not(.has-video):not(.has-cloud-x-responsive-media)').forEach((target) => {
        if (target.closest('[data-cloud-x-product-stage]') && root.querySelector('[data-cloud-x-product-media]')) {
          target.replaceChildren();
          return;
        }
        const placeholder = document.createElement('span');
        placeholder.className = 'cloud-x-production-placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        target.replaceChildren(placeholder);
        target.classList.add('cloud-x-missing-production-media');
      });
    }
  }

  // A real product page groups its 18 modular Sections by product id. Shopify's
  // theme editor can temporarily render a Section without product context, so
  // product.id is not a safe controller key there. Use one stable page-level
  // preview key instead of falling back to each Section id.
  const controllerKeyForRoot = (root) => {
    const productId = String(root?.dataset.cloudXProductId || '').trim();
    if (productId) return `product:${productId}`;
    const productHandle = String(root?.dataset.cloudXProductHandle || '').trim();
    const pageContext = productHandle || window.location.pathname || 'theme-editor';
    return `preview:${pageContext}`;
  };
  const rootsForController = (controllerKey) => [...document.querySelectorAll('.cloud-x-pdp')]
    .filter((candidate) => controllerKeyForRoot(candidate) === controllerKey);

  function init(seedRoot) {
    if (!seedRoot) return;
    const controllerKey = controllerKeyForRoot(seedRoot);
    if (controllers.has(controllerKey)) return;

    const roots = rootsForController(controllerKey);
    if (!roots.length) return;
    const root = roots.find((candidate) => candidate.matches('.cloud-x-module--hero')) || roots[0];
    roots.forEach((moduleRoot) => {
      installResponsiveImages(moduleRoot);
      moduleRoot.dataset.cloudXInitialized = 'true';
    });

    const abortController = new AbortController();
    const observers = [];
    const timers = [];
    let toastTimer = 0;
    const listen = (target, eventName, handler, options = {}) => {
      if (!target) return;
      target.addEventListener(eventName, handler, { ...options, signal: abortController.signal });
    };

    const one = (selector) => {
      for (const moduleRoot of roots) {
        const match = moduleRoot.querySelector(selector);
        if (match) return match;
      }
      return null;
    };
    const all = (selector) => roots.flatMap((moduleRoot) => [...moduleRoot.querySelectorAll(selector)]);
    const moduleFor = (node) => node?.closest('.cloud-x-module') || root;
    const moduleSetting = (node, key, fallback = '') => moduleFor(node)?.dataset?.[key] ?? fallback;
    const heroRoot = roots.find((candidate) => candidate.matches('.cloud-x-module--hero'));
    const stage = one('[data-cloud-x-product-stage]');
    const form = one('.cloud-x-product-form');
    const variantInput = one('[data-cloud-x-variant-id]');
    const variantsNode = one('[data-cloud-x-variants]');
    const compatibilityNodes = all('[data-cloud-x-compatibility-data]');
    const compatibilityNode = compatibilityNodes.find((node) => {
      const value = node.textContent.trim();
      return value && value !== '[]';
    }) || compatibilityNodes[0];
    const i18nNode = one('[data-cloud-x-i18n]');
    const sticky = one('[data-cloud-x-sticky]');
    const toast = one('[data-cloud-x-toast]');
    const buy = one('[data-cloud-x-buy]');
    const finalCtas = all('[data-cloud-x-final-cta]');
    const mediaItems = all('[data-cloud-x-product-media]');
    const thumbButtons = all('.thumb');
    const groupGalleryByVariant =
  root.dataset.cloudXGroupMediaByVariant === 'true';

const visibleThumbButtons = () =>
  thumbButtons.filter((button) => !button.hidden);

const mediaPositionForVariant = (variant) => {
  if (!variant?.featured_media_id) return 0;

  const mediaItem = mediaItems.find(
    (item) =>
      String(item.dataset.cloudXMediaId) ===
      String(variant.featured_media_id)
  );

  return Number(
    mediaItem?.dataset.cloudXMediaPosition || 0
  );
};

const galleryRangeForVariant = (variant) => {
  if (!groupGalleryByVariant) return null;

  const start = mediaPositionForVariant(variant);

  if (!start) return null;

  const groupStarts = [
    ...new Set(
      variants
        .map(mediaPositionForVariant)
        .filter((position) => position > 0)
    )
  ].sort((a, b) => a - b);

  const nextStart =
    groupStarts.find((position) => position > start) ||
    Infinity;

  return {
    start,
    end: nextStart - 1
  };
};

const syncVariantGallery = (variant) => {
  if (!groupGalleryByVariant) return;

  const range = galleryRangeForVariant(variant);

  if (!range) return;

  mediaItems.forEach((item) => {
    const position =
      Number(item.dataset.cloudXMediaPosition || 0);

    item.hidden = !(
      position >= range.start &&
      position <= range.end
    );
  });

  thumbButtons.forEach((button) => {
    const position =
      Number(button.dataset.cloudXMediaPosition || 0);

    button.hidden = !(
      position >= range.start &&
      position <= range.end
    );

    if (button.hidden) {
      button.classList.remove('active');
      button.setAttribute('aria-pressed', 'false');
    }
  });

  if (galleryThumbRail) {
    galleryThumbRail.scrollLeft = 0;
  }
};
    const galleryThumbRail = one('.gallery-thumbs');
    /* Hero native video: allow first click on video body to start playback */
mediaItems.forEach((item) => {
  const video = item.querySelector('video');

  if (!video) return;

  video.style.cursor = 'pointer';

  /*
    Once the video has successfully started,
    let the browser keep its normal native controls behaviour.
  */
  listen(video, 'play', () => {
    video.dataset.cloudXStarted = 'true';
  });

  listen(video, 'click', (event) => {

    /* Already started before → keep native play / pause behaviour */
    if (video.dataset.cloudXStarted === 'true') return;

    const rect = video.getBoundingClientRect();

    /*
      Do not interfere with the browser's native control bar.
      Clicking the bottom controls still works normally.
    */
    const nativeControlsHeight = 60;

    if (event.clientY >= rect.bottom - nativeControlsHeight) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    video.play().catch(() => {});
  });
});
    const horizontalScrollRegions = all('[data-cloud-x-horizontal-scroll]');
    const modeOrder = thumbButtons.map((button) => button.dataset.mode).filter(Boolean);
    let variants = [];
    let compatibilityEntries = [];
    let compatibilityOptionName = '';
    let compatibilityUsesMatrix = false;
    let i18n = {};
    let pendingCtaSource = 'hero';
    let previousVariantId = null;
    let headerFrame = 0;

    // Futurer 3.1.0 can use either its native absolute Header or the active
    // Section Store Header #13, which is fixed and therefore outside document
    // flow. Measure the rendered overlay instead of assuming a theme-wide
    // breakpoint or hard-coded Header height.
    if (heroRoot) {
      const headerCandidates = () => [...new Set(
        [...document.querySelectorAll('.shopify-section-group-header-group')].flatMap((section) => [
          section.firstElementChild,
          ...section.querySelectorAll('.fixed, .xo-header, #xo-announcement-bar')
        ]).filter(Boolean)
      )];
      const syncHeaderSafeOffset = () => {
        window.cancelAnimationFrame(headerFrame);
        headerFrame = window.requestAnimationFrame(() => {
          const overlayBottom = headerCandidates().reduce((maximum, candidate) => {
            const style = window.getComputedStyle(candidate);
            if (style.display === 'none' || style.visibility === 'hidden') return maximum;
            if (!['fixed', 'absolute', 'sticky'].includes(style.position)) return maximum;
            const rect = candidate.getBoundingClientRect();
            if (rect.height < 1 || rect.width < 1) return maximum;
            const configuredTop = Number.parseFloat(style.top);
            const top = Number.isFinite(configuredTop) ? Math.max(0, configuredTop) : Math.max(0, rect.top);
            return Math.max(maximum, top + rect.height);
          }, 0);
          const breathingRoom = window.matchMedia('(max-width: 760px)').matches ? 6 : 8;
          heroRoot.style.setProperty('--cloud-x-header-safe-offset', `${Math.ceil(overlayBottom + breathingRoom)}px`);
        });
      };
      syncHeaderSafeOffset();
      listen(window, 'resize', syncHeaderSafeOffset, { passive: true });
      if ('ResizeObserver' in window) {
        const headerObserver = new ResizeObserver(syncHeaderSafeOffset);
        headerCandidates().forEach((candidate) => headerObserver.observe(candidate));
        observers.push(headerObserver);
      }
    }

    try {
      variants = JSON.parse(variantsNode?.textContent || '[]');
      const compatibilityPayload = JSON.parse(compatibilityNode?.textContent || '[]');
      compatibilityEntries = Array.isArray(compatibilityPayload) ? compatibilityPayload : compatibilityPayload.entries || [];
      compatibilityOptionName = Array.isArray(compatibilityPayload) ? '' : compatibilityPayload.option_name || '';
      compatibilityUsesMatrix = !Array.isArray(compatibilityPayload) && compatibilityPayload.source === 'matrix';
      i18n = JSON.parse(i18nNode?.textContent || '{}');
    } catch (error) {
      console.error('Cloud X product data could not be parsed.', error);
    }

    const message = (key, replacements = {}) => Object.entries(replacements).reduce(
      (text, [token, value]) => String(text)
        .replaceAll(`{{ ${token} }}`, value)
        .replaceAll(`{{${token}}}`, value)
        .replaceAll(`__${token.toUpperCase()}__`, value),
      i18n[key] || ''
    );

    const modeData = Object.fromEntries(thumbButtons.map((button) => {
      const mode = button.dataset.mode;
      const translated = String(i18n[`mode_${mode}`] || '').split('|');
      const fallback = defaultModeData[mode] || [
        button.querySelector('strong')?.textContent?.trim() || '',
        button.dataset.cloudXCardMetric || '',
        button.dataset.cloudXCardTitle || '',
        button.dataset.cloudXCardText || ''
      ];
      const data = translated.length === 4 ? translated : fallback;
      if (button.dataset.cloudXCardMetric !== undefined) {
        data[0] = button.querySelector('strong')?.textContent?.trim() || data[0];
        data[1] = button.dataset.cloudXCardMetric || data[1];
        data[2] = button.dataset.cloudXCardTitle || data[2];
        data[3] = button.dataset.cloudXCardText || data[3];
      }
      return [mode, data];
    }));

    const trackingEnabled = root.dataset.cloudXTracking === 'true';
    const eventPrefix = root.dataset.cloudXEventPrefix || 'cloud_x';
    const emitAnalytics = (action, details = {}) => {
      if (!trackingEnabled) return;
      const payload = {
        event: `${eventPrefix}_${action}`,
        cloud_x: {
          action,
          product_id: root.dataset.cloudXProductId,
          product_handle: root.dataset.cloudXProductHandle,
          product_title: root.dataset.cloudXProductTitle,
          ...details
        }
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      window.dispatchEvent(new CustomEvent('cloud-x:analytics', { detail: payload.cloud_x }));
    };

    const showToast = (text) => {
      if (!toast) return;
      toast.textContent = text;
      toast.classList.add('show');
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => toast.classList.remove('show'), 2400);
    };

    const setHeroColor = (value) => {
      if (!stage) return;
      stage.dataset.color = String(value || '').toLowerCase().includes('white') ? 'white' : 'black';
    };

    const activateProductMedia = (item) => {
      mediaItems.forEach((mediaItem) => {
        const active = mediaItem === item;
        mediaItem.classList.toggle('active', active);
        mediaItem.setAttribute('aria-hidden', String(!active));
        if (!active) mediaItem.querySelector('video')?.pause();
      });
      stage?.classList.toggle('has-active-product-media', Boolean(item));
    };

    const activateMediaForMode = (mode) => {
      activateProductMedia(mediaItems.find((item) => item.dataset.cloudXMediaMode === mode));
    };

    const updateModeCopy = (mode) => {
      const mediaItem = mediaItems.find((item) => item.dataset.cloudXMediaMode === mode);
      const data = modeData[mode] || (mediaItem ? [
        mediaItem.dataset.cloudXMediaLabel || '',
        mediaItem.dataset.cloudXMediaMetric || '',
        mediaItem.dataset.cloudXMediaTitle || '',
        mediaItem.dataset.cloudXMediaText || ''
      ] : null);
      if (!data) return;
      const metric = one('[data-cloud-x-mode-metric]');
      const title = one('[data-cloud-x-mode-title]');
      const text = one('[data-cloud-x-mode-text]');
      const count = one('[data-cloud-x-media-count]');
      if (metric) {
        metric.textContent = data[1] || '';
        metric.hidden = !data[1];
      }
      if (title) {
        title.textContent = data[2] || data[0] || '';
        title.hidden = !(data[2] || data[0]);
      }
      if (text) {
        text.textContent = data[3] || '';
        text.hidden = !data[3];
      }
      if (count) count.textContent = `${Math.max(1, modeOrder.indexOf(mode) + 1)} / ${modeOrder.length || 1}`;
    };

    const optionSelects = () => all('.cloud-x-option-select')
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));

    const selectedVariant = () => {
      const selectedOptions = optionSelects().map((select) => select.value);
      return variants.find((item) => item.options.every((value, index) => value === selectedOptions[index]));
    };

    const syncOptionControls = (variant) => {
      if (!variant) return;
      optionSelects().forEach((select, index) => {
        select.value = variant.options[index];
        all(`[data-cloud-x-option-button][data-option-position="${selectorEscape(index + 1)}"]`).forEach((button) => {
          const active = button.dataset.optionValue === variant.options[index];
          button.classList.toggle('active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      });
      const colorSelect = optionSelects().find((select) => normalizedOptionName(select.dataset.optionName) === normalizedOptionName(root.dataset.cloudXColorOptionName))
        || one('.cloud-x-color-select');
      if (colorSelect) {
        const colorName = one('[data-cloud-x-color-name]');
        if (colorName) colorName.textContent = colorSelect.value;
        setHeroColor(colorSelect.value);
      }
    };

    const updateOptionAvailability = () => {
      const selects = optionSelects();
      const selected = selects.map((select) => select.value);
      const configuredModel = normalizedOptionName(root.dataset.cloudXModelOptionName);
      let modelPosition = selects.findIndex((select) => normalizedOptionName(select.dataset.optionName) === configuredModel);
      if (modelPosition < 0) modelPosition = selects.findIndex((select) => /(model|device|ipad|generation|size|compat)/.test(normalizedOptionName(select.dataset.optionName)));
      selects.forEach((select, position) => {
        [...select.options].forEach((option) => {
          const matching = variants.filter((variant) => variant.options[position] === option.value
            && (position === modelPosition || variant.options.every((value, index) => {
              if (index === position) return true;
              if (modelPosition >= 0 && index === modelPosition) return value === selected[index];
              return value === selected[index];
            })));
          const exists = matching.length > 0;
          const available = matching.some((variant) => variant.available);
          option.disabled = !exists;
          const button = one(`[data-cloud-x-option-button][data-option-position="${selectorEscape(position + 1)}"][data-option-value="${selectorEscape(option.value)}"]`);
          if (button) {
            button.disabled = !exists;
            button.classList.toggle('unavailable', !exists);
            button.classList.toggle('sold-out', exists && !available);
            button.setAttribute('aria-disabled', String(!exists));
          }
        });
      });
    };

    const selectOptionValue = (position, value) => {
      const selects = optionSelects();
      const index = Number(position) - 1;
      if (!selects[index]) return null;
      selects[index].value = value;
      let variant = selectedVariant();
      if (!variant) {
        const candidates = variants.filter((item) => item.options[index] === value);
        variant = candidates.find((item) => item.available) || candidates[0];
        if (variant) syncOptionControls(variant);
      }
      return updateVariant();
    };

    const updateVariant = () => {
      if (!form || !variantInput || variants.length === 0) return null;
      const variant = selectedVariant();
      const addButton = one('.cloud-x-product-form .btn-primary');
      const paymentContainer = one('[data-cloud-x-payment-button]');
      const buyNowButtons = all('[data-cloud-x-payment-button] button');
      const stickyButton = one('[data-cloud-x-sticky-add]');
      const available = Boolean(variant?.available);

      updateOptionAvailability();
      if (!variant) {
  if (addButton) addButton.disabled = true;

  buyNowButtons.forEach((button) => {
    button.disabled = true;
  });

  if (stickyButton) {
    stickyButton.disabled = true;
  }

  return null;
}

      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      all('[data-cloud-x-price]').forEach((node) => { node.textContent = variant.price_formatted; });
      all('[data-cloud-x-compare-price]').forEach((node) => {
        node.hidden = !(variant.compare_at_price > variant.price);
        node.textContent = variant.compare_at_price_formatted;
      });
      all('[data-cloud-x-saving]').forEach((node) => {
        node.hidden = !(variant.compare_at_price > variant.price);
        node.textContent = `${i18n.saving_prefix || 'SAVE'} ${variant.saving_formatted}`;
      });
      const stickySub = one('[data-cloud-x-sticky-sub]');
      if (stickySub) stickySub.textContent = variant.title;
      const stickyImage = one('[data-cloud-x-sticky-image]');

const stickyColorSelect =
  optionSelects().find(
    (select) =>
      normalizedOptionName(select.dataset.optionName) ===
      normalizedOptionName(root.dataset.cloudXColorOptionName)
  ) || one('.cloud-x-color-select');

if (stickyImage && stickyColorSelect) {
  const color = normalizedOptionName(stickyColorSelect.value);

  let nextImage = '';

  if (color.includes('white')) {
    nextImage = stickyImage.dataset.softWhite;

  } else if (
    color.includes('lavender') ||
    color.includes('purple')
  ) {
    nextImage = stickyImage.dataset.lavenderPurple;

  } else if (color.includes('black')) {
    nextImage = stickyImage.dataset.jetBlack;
  }

  if (nextImage) {
    stickyImage.src = nextImage;
  }
}
      if (addButton) addButton.disabled = !available;
      buyNowButtons.forEach((button) => { button.disabled = !available; });
      if (paymentContainer) paymentContainer.setAttribute('aria-disabled', String(!available));
      if (stickyButton) stickyButton.disabled = !available;

      const addLabel = one('[data-cloud-x-add-label]');
      const stickyLabel = one('[data-cloud-x-sticky-label]');
      if (addLabel) addLabel.textContent = available ? (i18n.add_to_cart || 'Add to Cart') : (i18n.sold_out || 'Sold Out');
      if (stickyLabel) stickyLabel.textContent = available ? (i18n.add_to_cart || 'Add to Cart') : (i18n.sold_out || 'Sold Out');

      syncVariantGallery(variant);
      const variantMedia = mediaItems.find((item) => String(item.dataset.cloudXMediaId) === String(variant.featured_media_id));
      if (variantMedia) {
        const mappedMode = variantMedia.dataset.cloudXMediaMode;
        const nextMode = modeOrder.includes(mappedMode) ? mappedMode : modeOrder[0];
        thumbButtons.forEach((item) => {
          const active = item.dataset.mode === mappedMode;
          item.classList.toggle('active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        if (stage) stage.dataset.mode = mappedMode;
        all('.hero-scene').forEach((scene) => scene.classList.toggle('active', scene.classList.contains(`scene-${nextMode}`)));
        activateProductMedia(variantMedia);
        updateModeCopy(mappedMode);
      } else {
        activateMediaForMode(stage?.dataset.mode || modeOrder[0]);
      }

      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', nextUrl);
      if (previousVariantId && String(previousVariantId) !== String(variant.id)) {
        emitAnalytics('select_variant', { variant_id: variant.id, variant_title: variant.title, available });
      }
      previousVariantId = variant.id;
      return variant;
    };

    syncVariantGallery(selectedVariant());

const initialVisibleThumbs =
  visibleThumbButtons();

const initialThumb =
  initialVisibleThumbs.find(
    (button) =>
      button.classList.contains('active')
  ) ||
  initialVisibleThumbs[0];
    if (initialThumb) {
      thumbButtons.forEach((button) => {
        const active = button === initialThumb;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      if (stage) stage.dataset.mode = initialThumb.dataset.mode;
      updateModeCopy(initialThumb.dataset.mode);
    }

    thumbButtons.forEach((button) => listen(button, 'click', () => {
      thumbButtons.forEach((item) => {
        item.classList.remove('active');
        item.setAttribute('aria-pressed', 'false');
      });
      button.classList.add('active');
      button.setAttribute('aria-pressed', 'true');
      if (galleryThumbRail && galleryThumbRail.scrollWidth > galleryThumbRail.clientWidth) {

  /* Desktop: ESR-style 6-thumbnail sliding window */
  if (window.matchMedia('(min-width: 761px)').matches) {

    const railThumbs = [...galleryThumbRail.querySelectorAll('.thumb')];
    const selectedIndex = railThumbs.indexOf(button);

    const visibleCount = window.matchMedia('(max-width: 1160px)').matches ? 4 : 6;

    const railStyle = window.getComputedStyle(galleryThumbRail);
    const gap =
      parseFloat(railStyle.columnGap) ||
      parseFloat(railStyle.gap) ||
      8;

    const step = button.offsetWidth + gap;

    const currentStart = Math.round(
      galleryThumbRail.scrollLeft / step
    );

    const maxStart = Math.max(
      0,
      railThumbs.length - visibleCount
    );

    let nextStart = currentStart;

    /*
      Selected the right-most visible thumbnail:
      move the window forward by one item.
    */
    if (selectedIndex >= currentStart + visibleCount - 1) {
      nextStart = Math.min(
        maxStart,
        selectedIndex - visibleCount + 2
      );
    }

    /*
      Selected the left-most visible thumbnail:
      move the window backward by one item.
    */
    if (selectedIndex <= currentStart) {
      nextStart = Math.max(
        0,
        selectedIndex - 1
      );
    }

    galleryThumbRail.scrollTo({
      left: nextStart * step,
      behavior: 'smooth'
    });

  } else {

    /* Keep the current mobile behaviour */
    const targetLeft =
      button.offsetLeft
      - galleryThumbRail.offsetLeft
      - ((galleryThumbRail.clientWidth - button.offsetWidth) / 2);

    galleryThumbRail.scrollTo({
      left: targetLeft,
      behavior: 'smooth'
    });

  }
}
      const mode = button.dataset.mode;
      if (stage) stage.dataset.mode = mode;
      all('.hero-scene').forEach((scene) => scene.classList.toggle('active', scene.classList.contains(`scene-${mode}`)));
      activateMediaForMode(mode);
      updateModeCopy(mode);
      const mediaItem = mediaItems.find((item) => item.dataset.cloudXMediaMode === mode);
      emitAnalytics('view_media', {
        media_mode: mode,
        media_id: mediaItem?.dataset.cloudXMediaId,
        media_type: mediaItem?.dataset.cloudXMediaType
      });
    }));

    const activateHeroIndex = (
  requestedIndex,
  userInitiated = true
) => {

  const buttons = visibleThumbButtons();

  if (!buttons.length) return;

  const currentIndex = Math.max(
    0,
    buttons.findIndex(
      (button) =>
        button.classList.contains('active')
    )
  );

  const shouldLoop =
    root.dataset.cloudXLoop !== 'false';

  let nextIndex =
    Number.isFinite(requestedIndex)
      ? requestedIndex
      : currentIndex;

  if (shouldLoop) {
    nextIndex =
      (nextIndex + buttons.length) %
      buttons.length;
  } else {
    nextIndex = Math.max(
      0,
      Math.min(buttons.length - 1, nextIndex)
    );
  }

  buttons[nextIndex]?.click();

  const prev =
    one('[data-cloud-x-hero-prev]');

  const next =
    one('[data-cloud-x-hero-next]');

  if (!shouldLoop) {
    if (prev) prev.disabled = nextIndex === 0;

    if (next) {
      next.disabled =
        nextIndex === buttons.length - 1;
    }
  }

  if (userInitiated) {
    emitAnalytics(
      'hero_navigation',
      {
        index: nextIndex + 1,
        total: buttons.length
      }
    );
  }
};

const currentVisibleHeroIndex = () => {
  const buttons = visibleThumbButtons();

  return Math.max(
    0,
    buttons.findIndex(
      (button) =>
        button.classList.contains('active')
    )
  );
};

    listen(one('[data-cloud-x-hero-prev]'), 'click', (event) => {
      event.stopPropagation();
      activateHeroIndex(
  currentVisibleHeroIndex() - 1
);
    });
    listen(one('[data-cloud-x-hero-next]'), 'click', (event) => {
      event.stopPropagation();
      activateHeroIndex(
  currentVisibleHeroIndex() + 1
);
    });

    const heroDragSurface = one('[data-cloud-x-hero-drag]');
    if (heroDragSurface && thumbButtons.length > 1) {
      let dragStartX = null;
      let dragPointerId = null;
      const finishDrag = (event, cancelled = false) => {
        if (dragStartX === null || (dragPointerId !== null && event.pointerId !== dragPointerId)) return;
        const distance = event.clientX - dragStartX;
        heroDragSurface.classList.remove('is-dragging');
        if (heroDragSurface.hasPointerCapture?.(event.pointerId)) heroDragSurface.releasePointerCapture(event.pointerId);
        dragStartX = null;
        dragPointerId = null;
        if (!cancelled && Math.abs(distance) >= Math.max(42, heroDragSurface.clientWidth * .075)) {
          const currentIndex = currentVisibleHeroIndex();
          activateHeroIndex(currentIndex + (distance < 0 ? 1 : -1));
        }
      };
      listen(heroDragSurface, 'pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button, a, input, select, textarea, video, iframe, model-viewer')) return;
        dragStartX = event.clientX;
        dragPointerId = event.pointerId;
        heroDragSurface.classList.add('is-dragging');
        heroDragSurface.setPointerCapture?.(event.pointerId);
      });
      listen(heroDragSurface, 'pointerup', (event) => finishDrag(event));
      listen(heroDragSurface, 'pointercancel', (event) => finishDrag(event, true));
      listen(heroDragSurface, 'lostpointercapture', (event) => finishDrag(event, true));
    }

    if (thumbButtons.length > 1 && root.dataset.cloudXAutoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const autoplayTimer = window.setInterval(() => {
        if (document.hidden || heroDragSurface?.matches(':hover')) return;
        const currentIndex = currentVisibleHeroIndex();
        activateHeroIndex(currentIndex + 1, false);
      }, Math.max(2, Number(root.dataset.cloudXAutoplaySeconds) || 5) * 1000);
      timers.push(autoplayTimer);
    }

    all('[data-cloud-x-day-track]').forEach((dayTrack) => {
      const dayModule = moduleFor(dayTrack);
      const dayNav = dayModule.querySelector('[data-cloud-x-day-nav]');
      const dayNavItems = [...dayModule.querySelectorAll('[data-cloud-x-day-jump]')];
      const dayCards = [...dayModule.querySelectorAll('[data-cloud-x-day-card]')];
      if (!dayNavItems.length || !dayCards.length) return;
      let daySyncFrame = 0;
      const keepDayNavItemVisible = (index) => {
        const item = dayNavItems[index];
        if (!dayNav || !item || dayNav.scrollWidth <= dayNav.clientWidth + 1) return;
        const navBounds = dayNav.getBoundingClientRect();
        const itemBounds = item.getBoundingClientRect();
        const leadingBoundary = navBounds.left + 8;
        const trailingBoundary = navBounds.right - 24;
        let delta = 0;
        if (itemBounds.left < leadingBoundary) delta = itemBounds.left - leadingBoundary;
        else if (itemBounds.right > trailingBoundary) delta = itemBounds.right - trailingBoundary;
        if (Math.abs(delta) > 1) dayNav.scrollBy({ left: delta, behavior: 'smooth' });
      };
      const setActiveDay = (index) => {
        dayNavItems.forEach((item, itemIndex) => {
          item.classList.toggle('is-active', itemIndex === index);
          if (itemIndex === index) item.setAttribute('aria-current', 'step');
          else item.removeAttribute('aria-current');
        });
        dayCards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === index));
        keepDayNavItemVisible(index);
      };
      const syncActiveDay = () => {
        daySyncFrame = 0;
        const trackCenter = dayTrack.scrollLeft + dayTrack.clientWidth / 2;
        let activeIndex = 0;
        let activeDistance = Number.POSITIVE_INFINITY;
        dayCards.forEach((card, index) => {
          const cardCenter = card.offsetLeft - dayTrack.offsetLeft + card.offsetWidth / 2;
          const distance = Math.abs(cardCenter - trackCenter);
          if (distance < activeDistance) {
            activeDistance = distance;
            activeIndex = index;
          }
        });
        setActiveDay(activeIndex);
      };
      dayNavItems.forEach((item, index) => listen(item, 'click', () => {
        const card = dayCards[index];
        if (!card) return;
        dayTrack.scrollTo({ left: card.offsetLeft - dayTrack.offsetLeft, behavior: 'smooth' });
        setActiveDay(index);
      }));
      listen(dayTrack, 'scroll', () => {
        if (daySyncFrame) return;
        daySyncFrame = window.requestAnimationFrame(syncActiveDay);
      }, { passive: true });
      listen(window, 'resize', syncActiveDay);
      syncActiveDay();
    });

    horizontalScrollRegions.forEach((region, regionIndex) => {
      if (region.closest('.gallery') || region.dataset.cloudXControlsInstalled === 'true') return;
      region.dataset.cloudXControlsInstalled = 'true';
      const controls = document.createElement('div');
      controls.className = 'cloud-x-carousel-controls';
      controls.dataset.cloudXGenerated = 'carousel-controls';
      const pagination = document.createElement('div');
      pagination.className = 'cloud-x-carousel-pagination';
      pagination.hidden = moduleSetting(region, 'cloudXShowPagination') !== 'true';
      const prev = document.createElement('button');
      const next = document.createElement('button');
      prev.type = next.type = 'button';
      prev.className = next.className = 'cloud-x-carousel-button';
      prev.setAttribute('aria-label', 'Previous items');
      next.setAttribute('aria-label', 'Next items');
      prev.textContent = '‹';
      next.textContent = '›';
      const scrollAmount = () => {
        const item = region.firstElementChild;
        const itemWidth = item?.getBoundingClientRect().width || Math.max(220, region.clientWidth * .82);
        const gap = Number.parseFloat(getComputedStyle(region).columnGap || getComputedStyle(region).gap) || 0;
        return (itemWidth + gap) * Math.max(1, Number(moduleSetting(region, 'cloudXItemsPerMove', 1)) || 1);
      };
      listen(prev, 'click', () => region.scrollBy({ left: -scrollAmount(), behavior: 'smooth' }));
      listen(next, 'click', () => region.scrollBy({ left: scrollAmount(), behavior: 'smooth' }));
      controls.append(pagination, prev, next);
      region.after(controls);
      const updateControls = () => {
        const max = Math.max(0, region.scrollWidth - region.clientWidth);
        const automaticControls = region.dataset.cloudXScrollControls === 'auto';
        if (automaticControls) controls.hidden = max <= 2;
        const progress = max ? region.scrollLeft / max : 0;
        const pages = Math.max(1, Math.ceil(region.scrollWidth / Math.max(1, region.clientWidth)));
        if (pagination.childElementCount !== pages) {
          pagination.replaceChildren(...Array.from({ length: pages }, (_, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'cloud-x-carousel-dot';
            dot.setAttribute('aria-label', `Go to page ${index + 1}`);
            listen(dot, 'click', () => region.scrollTo({ left: index * region.clientWidth, behavior: 'smooth' }));
            return dot;
          }));
        }
        const activePage = Math.min(pages - 1, Math.round(progress * Math.max(0, pages - 1)));
        [...pagination.children].forEach((dot, index) => dot.classList.toggle('active', index === activePage));
        if (automaticControls || moduleSetting(region, 'cloudXLoop', 'true') === 'false') {
          prev.disabled = region.scrollLeft <= 2;
          next.disabled = region.scrollLeft >= max - 2;
        }
      };
      listen(region, 'scroll', updateControls, { passive: true });
      listen(window, 'resize', updateControls);
      updateControls();
      if (moduleSetting(region, 'cloudXAutoplay') === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const timer = window.setInterval(() => {
          if (document.hidden || region.matches(':hover')) return;
          const max = Math.max(0, region.scrollWidth - region.clientWidth);
          if (moduleSetting(region, 'cloudXLoop', 'true') !== 'false' && region.scrollLeft >= max - 2) region.scrollTo({ left: 0, behavior: 'smooth' });
          else region.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
        }, Math.max(2, Number(moduleSetting(region, 'cloudXAutoplaySeconds', 5)) || 5) * 1000);
        timers.push(timer);
      }
    });

    horizontalScrollRegions.forEach((region) => listen(region, 'keydown', (event) => {
      if (event.target !== region || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const rtlMultiplier = getComputedStyle(region).direction === 'rtl' ? -1 : 1;
      region.scrollBy({ left: direction * rtlMultiplier * Math.max(220, region.clientWidth * .8), behavior: 'smooth' });
    }));

    const faqItems = all('[data-cloud-x-faq-category]');
    const faqCategories = [...new Set(faqItems.map((item) => item.dataset.cloudXFaqCategory).filter(Boolean))];
    if (faqCategories.length > 1) {
      const tabs = document.createElement('div');
      tabs.className = 'cloud-x-faq-tabs';
      tabs.dataset.cloudXGenerated = 'faq-tabs';
      const labels = ['all', ...faqCategories];
      labels.forEach((category, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cloud-x-faq-tab';
        button.textContent = category === 'all' ? 'All' : category.replaceAll('-', ' ');
        button.classList.toggle('active', index === 0);
        button.setAttribute('aria-pressed', String(index === 0));
        listen(button, 'click', () => {
          [...tabs.children].forEach((item) => {
            const active = item === button;
            item.classList.toggle('active', active);
            item.setAttribute('aria-pressed', String(active));
          });
          faqItems.forEach((item) => { item.hidden = category !== 'all' && item.dataset.cloudXFaqCategory !== category; });
        });
        tabs.append(button);
      });
      faqItems[0].before(tabs);
    }

    all('[data-cloud-x-option-button]').forEach((button) => listen(button, 'click', () => {
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const position = button.dataset.optionPosition;
      const select = one(`.cloud-x-option-select[data-option-position="${selectorEscape(position)}"]`);
      if (!select) return;
      select.value = button.dataset.optionValue;
      all(`[data-cloud-x-option-button][data-option-position="${selectorEscape(position)}"]`).forEach((item) => {
        const active = item === button;
        item.classList.toggle('active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      const colorName = one('[data-cloud-x-color-name]');
      if (colorName) colorName.textContent = button.dataset.optionValue;
      setHeroColor(button.dataset.optionValue);
      selectOptionValue(position, button.dataset.optionValue);
    }));

    all('.cloud-x-option-select').forEach((select) => listen(select, 'change', () => {
      selectOptionValue(select.dataset.optionPosition, select.value);
    }));
    /* =========================================
   Quantity — Hero + Sticky CTA synchronization
   ========================================= */

const quantityInput = one('[data-cloud-x-quantity]');
const stickyQuantityInput = one('[data-cloud-x-sticky-quantity]');

const clampQuantity = (value) => {
  const minimum =
    Number(quantityInput?.min || stickyQuantityInput?.min) || 1;

  const maximum =
    Number(quantityInput?.max || stickyQuantityInput?.max) || 99;

  return Math.max(
    minimum,
    Math.min(
      maximum,
      Number(value) || minimum
    )
  );
};

const setQuantity = (value) => {
  const next = clampQuantity(value);

  if (quantityInput) {
    quantityInput.value = String(next);
  }

  if (stickyQuantityInput) {
    stickyQuantityInput.value = String(next);
  }

  return next;
};


/* =========================
   HERO QUANTITY
   ========================= */

listen(
  one('[data-cloud-x-quantity-minus]'),
  'click',
  () => {
    const current = Number(quantityInput?.value) || 1;
    const next = setQuantity(current - 1);

    if (quantityInput) {
      quantityInput.value = String(next);
      quantityInput.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }
);

listen(
  one('[data-cloud-x-quantity-plus]'),
  'click',
  () => {
    const current = Number(quantityInput?.value) || 1;
    const next = setQuantity(current + 1);

    if (quantityInput) {
      quantityInput.value = String(next);
      quantityInput.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }
);


/* Manual Hero quantity → sync Sticky */
listen(
  quantityInput,
  'change',
  () => {
    setQuantity(quantityInput.value);
  }
);


/* =========================
   STICKY QUANTITY
   ========================= */

listen(
  one('[data-cloud-x-sticky-quantity-minus]'),
  'click',
  () => {
    const current =
      Number(stickyQuantityInput?.value) || 1;

    const next = setQuantity(current - 1);

    if (quantityInput) {
      quantityInput.value = String(next);
      quantityInput.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }
);

listen(
  one('[data-cloud-x-sticky-quantity-plus]'),
  'click',
  () => {
    const current =
      Number(stickyQuantityInput?.value) || 1;

    const next = setQuantity(current + 1);

    if (quantityInput) {
      quantityInput.value = String(next);
      quantityInput.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }
);

listen(
  stickyQuantityInput,
  'change',
  () => {
    const next = setQuantity(stickyQuantityInput.value);

    if (quantityInput) {
      quantityInput.value = String(next);
      quantityInput.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }
);


/* Initial quantity sync */
setQuantity(quantityInput?.value || 1);



    all('[data-scroll]').forEach((button) => listen(button, 'click', () => {
      pendingCtaSource = button.dataset.cloudXCtaSource || pendingCtaSource;
      emitAnalytics('click_cta', { source: pendingCtaSource });
      (one(button.dataset.scroll) || document.querySelector(button.dataset.scroll))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
    all('[data-cloud-x-cta-source]').forEach((button) => listen(button, 'click', () => {
      pendingCtaSource = button.dataset.cloudXCtaSource || 'hero';
    }));
    listen(one('[data-cloud-x-payment-button]'), 'click', () => {
      const selected = variants.find((item) => String(item.id) === String(variantInput?.value));
      if (!selected?.available) return;
      emitAnalytics('buy_now_click', {
        source: 'buy_now',
        variant_id: selected.id,
        variant_title: selected.title,
        value: selected.price / 100,
        currency: root.dataset.currency
      });
    });

    all('[data-cloud-x-load-video]').forEach((button) => listen(button, 'click', () => {
      const template = button.parentElement?.querySelector('[data-cloud-x-video-template]');
      if (!template) return;
      const fragment = template.content.cloneNode(true);
      const ugcCard = button.closest('[data-cloud-x-ugc-card]')?.dataset.cloudXUgcCard;
      template.remove();
      button.replaceWith(fragment);
      if (ugcCard) emitAnalytics('ugc_play', { card: ugcCard });
    }, { once: true }));

    const classifyFamily = (model) => {
      const value = String(model || '').toLowerCase();
      if (value.includes('ipad mini') || /\bmini\b/.test(value)) return 'iPad mini';
      if (value.includes('ipad air') || /\bair\b/.test(value)) return 'iPad Air';
      if (value.includes('ipad pro') || /^\s*pro\b/.test(value)) return 'iPad Pro';
      if (value.includes('ipad')) return 'iPad';
      return 'Other models';
    };

    const configuredModelName = normalizedOptionName(compatibilityOptionName || root.dataset.cloudXModelOptionName);
    const configuredColorName = normalizedOptionName(root.dataset.cloudXColorOptionName);
    const modelSelect = optionSelects().find((select) => normalizedOptionName(select.dataset.optionName) === configuredModelName)
      || optionSelects().find((select) => {
        const name = normalizedOptionName(select.dataset.optionName);
        return name !== configuredColorName && /(model|device|ipad|generation|size|compat)/.test(name);
      })
      || optionSelects().find((select) => normalizedOptionName(select.dataset.optionName) !== configuredColorName);

    all('[data-cloud-x-compat]').forEach((card) => {
      const familySelect = card.querySelector('[data-cloud-x-family]');
      const sizeSelect = card.querySelector('[data-cloud-x-size]');
      const generationSelect = card.querySelector('[data-cloud-x-generation]');
      const modelNumberInput = card.querySelector('[data-cloud-x-model-number]');
      const checkButton = card.querySelector('[data-cloud-x-check-compat]');
      const result = card.querySelector('[data-cloud-x-compat-result]');
      if (!familySelect || !sizeSelect || !generationSelect || !checkButton || !result) return;

      const modelPosition = Number(modelSelect?.dataset.optionPosition || 0) - 1;
      const modelValues = modelPosition >= 0
        ? [...new Set(variants.map((variant) => variant.options[modelPosition]).filter(Boolean))]
        : [];
      const entries = compatibilityEntries.length > 0
        ? compatibilityEntries
          .map((entry) => ({ ...entry, option_value: optionValueForEntry(entry, modelValues) }))
          .filter((entry) => compatibilityEntryKey(entry))
        : compatibilityUsesMatrix
          ? []
          : modelValues.map((value) => ({ model_key: value, model_number: '', display_name: value, family: classifyFamily(value), option_value: value, compatible: true }));
      const familyOrder = ['iPad Pro', 'iPad Air', 'iPad', 'iPad mini', 'Other models'];
      const families = [...new Set(entries.map((entry) => entry.family || classifyFamily(entry.display_name)).filter(Boolean))]
        .sort((a, b) => {
          const aIndex = familyOrder.indexOf(a);
          const bIndex = familyOrder.indexOf(b);
          return (aIndex < 0 ? familyOrder.length : aIndex) - (bIndex < 0 ? familyOrder.length : bIndex) || a.localeCompare(b);
        });

      if (!modelSelect || families.length === 0) {
        result.textContent = i18n.unavailable || 'Compatibility options are not available for this product yet.';
        return;
      }

      const entriesForFamily = (family) => entries.filter((entry) => (entry.family || classifyFamily(entry.display_name)) === family);
      const populateModels = (family, size, selectedValue = '') => {
        generationSelect.replaceChildren(new Option(i18n.select_generation || 'Select exact model / generation', ''));
        const seen = new Set();
        entriesForFamily(family)
          .filter((entry) => screenSizeForEntry(entry) === size)
          .forEach((entry) => {
            const entryKey = compatibilityEntryKey(entry);
            if (!entryKey || seen.has(entryKey)) return;
            seen.add(entryKey);
            generationSelect.add(new Option(entry.display_name || entry.generation || entry.option_value, entryKey));
          });
        generationSelect.disabled = !family || !size;
        if (selectedValue) generationSelect.value = selectedValue;
        checkButton.disabled = !generationSelect.value;
      };

      const populateSizes = (family, selectedSize = '', selectedValue = '') => {
        sizeSelect.replaceChildren(new Option(i18n.select_size || 'Select screen size', ''));
        const sizes = [...new Set(entriesForFamily(family).map(screenSizeForEntry))]
          .sort((a, b) => Number.parseFloat(a) - Number.parseFloat(b) || a.localeCompare(b));
        sizes.forEach((size) => sizeSelect.add(new Option(size, size)));
        sizeSelect.disabled = !family;
        if (selectedSize) sizeSelect.value = selectedSize;
        populateModels(family, sizeSelect.value, selectedValue);
      };

      families.forEach((family) => familySelect.add(new Option(family, family)));
      listen(familySelect, 'change', () => {
        populateSizes(familySelect.value);
        if (modelNumberInput) modelNumberInput.value = '';
        result.textContent = '';
      });
      listen(sizeSelect, 'change', () => {
        populateModels(familySelect.value, sizeSelect.value);
        if (modelNumberInput) modelNumberInput.value = '';
        result.textContent = '';
      });
      listen(generationSelect, 'change', () => {
        checkButton.disabled = !generationSelect.value;
        if (modelNumberInput) modelNumberInput.value = '';
        result.textContent = '';
      });
      listen(modelNumberInput, 'input', () => {
        const modelNumber = normalizedModelNumber(modelNumberInput.value);
        if (!modelNumber) {
          checkButton.disabled = !generationSelect.value;
          result.textContent = '';
          return;
        }
        const entry = entries.find((item) => modelSearchTermsForEntry(item).includes(modelNumber));
        if (!entry) {
          checkButton.disabled = true;
          result.textContent = i18n.model_number_not_found || 'That model number is not in the compatibility list.';
          return;
        }
        const family = entry.family || classifyFamily(entry.display_name);
        const size = screenSizeForEntry(entry);
        familySelect.value = family;
        populateSizes(family, size, compatibilityEntryKey(entry));
        result.textContent = '';
      });
      listen(checkButton, 'click', () => {
        const selectedEntry = entries.find((entry) => compatibilityEntryKey(entry) === generationSelect.value);
        if (!selectedEntry) return;
        const displayName = selectedEntry.display_name || selectedEntry.generation || generationSelect.value;
        if (selectedEntry.compatible === false) {
          result.textContent = card.dataset.cloudXIncompatibleMessage || '';
          emitAnalytics('compatibility_check', { model: displayName, model_key: selectedEntry.model_key, result: 'incompatible' });
          return;
        }
        const modelValue = selectedEntry.option_value;
        if (!modelValue) {
          result.textContent = i18n.unavailable || 'Compatibility options are not available for this product yet.';
          emitAnalytics('compatibility_check', { model: displayName, model_key: selectedEntry.model_key, result: 'unmapped' });
          return;
        }
        const currentSelections = optionSelects().map((select) => select.value);
        const candidates = variants.filter((variant) => variant.options[modelPosition] === modelValue);
        const exactCandidate = candidates.find((variant) => variant.options.every((value, index) => index === modelPosition || value === currentSelections[index]));
        const candidate = exactCandidate || candidates.find((variant) => variant.available) || candidates[0];
        if (!candidate) {
          result.textContent = i18n.combination_unavailable || 'This model and color combination is unavailable. Please choose another option.';
          emitAnalytics('compatibility_check', { model: modelValue, result: 'unavailable' });
          return;
        }
        syncOptionControls(candidate);
        const variant = updateVariant();
        result.textContent = variant?.available
          ? message('compatible', { model: displayName })
          : message('model_sold_out', { model: displayName });
        emitAnalytics('compatibility_check', {
          model: modelValue,
          model_number: modelNumberInput?.value ? normalizedModelNumber(modelNumberInput.value) : undefined,
          variant_id: variant?.id,
          result: variant?.available ? 'compatible' : 'sold_out'
        });
        buy?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    let buyPassed = false;
    const finalVisibility = new Map();
    const footerVisibility = new Map();
    const updateSticky = () => {
      if (!sticky) return;
      const finalVisible = [...finalVisibility.values()].some(Boolean);
      const footerVisible = [...footerVisibility.values()].some(Boolean);
      const show = buyPassed && !finalVisible && !footerVisible;
      sticky.classList.toggle('show', show);
      sticky.setAttribute('aria-hidden', String(!show));
      sticky.inert = !show;
      roots.forEach((moduleRoot) => moduleRoot.classList.toggle('sticky-visible', show));
      document.body?.classList.toggle('cloud-x-sticky-visible', show);
    };
    if (buy && sticky && 'IntersectionObserver' in window) {
      const buyObserver = new IntersectionObserver(([entry]) => {
        buyPassed = entry.boundingClientRect.bottom < 90;
        updateSticky();
      }, { threshold: [0, 0.05, 1] });
      buyObserver.observe(buy);
      observers.push(buyObserver);
      finalCtas.forEach((finalCta) => {
        finalVisibility.set(finalCta, false);
        const finalObserver = new IntersectionObserver(([entry]) => {
          finalVisibility.set(finalCta, entry.isIntersecting);
          updateSticky();
        }, { threshold: 0.04 });
        finalObserver.observe(finalCta);
        observers.push(finalObserver);
      });
      [...document.querySelectorAll('footer, [role="contentinfo"], [id^="shopify-section-footer"]')].forEach((footer) => {
        footerVisibility.set(footer, false);
        const footerObserver = new IntersectionObserver(([entry]) => {
          footerVisibility.set(footer, entry.isIntersecting);
          updateSticky();
        }, { threshold: 0.01 });
        footerObserver.observe(footer);
        observers.push(footerObserver);
      });
    } else if (sticky) {
      sticky.inert = true;
    }

    const configuredCartAddUrl = root.dataset.cloudXCartAddUrl || `${window.Shopify?.routes?.root || '/'}cart/add`;
    const cartAddUrl = configuredCartAddUrl.endsWith('.js') ? configuredCartAddUrl : `${configuredCartAddUrl}.js`;
    const cartUrl = root.dataset.cloudXCartUrl || `${window.Shopify?.routes?.root || '/'}cart`;
    const cartBehavior = root.dataset.cloudXCartBehavior || 'auto';
    const cartErrorRegion = one('[data-cloud-x-cart-error]');
    const setCartError = (errorText = '') => {
      if (!cartErrorRegion) return;
      cartErrorRegion.textContent = errorText;
      cartErrorRegion.hidden = !errorText;
    };
    const cartSurfaces = () => [...document.querySelectorAll('cart-drawer, cart-notification, xo-cart-mini, [data-cart-drawer]')];
    const cartSectionIds = () => {
      const ids = new Set();
      cartSurfaces().forEach((surface) => {
        if (typeof surface.getSectionsToRender === 'function') {
          try {
            surface.getSectionsToRender().forEach((entry) => {
              if (entry?.id) ids.add(entry.id);
              if (entry?.section) ids.add(entry.section);
            });
          } catch { /* Theme adapter is optional. */ }
        }
        if (surface.id) ids.add(surface.id.replace(/^shopify-section-/, ''));
        const section = surface.closest('[id^="shopify-section-"]');
        if (section?.id) ids.add(section.id.replace(/^shopify-section-/, ''));
      });
      return [...ids].filter(Boolean).slice(0, 5);
    };
    const updateCartCount = (cartData) => {
  if (!cartData || !Number.isFinite(Number(cartData.item_count))) return;

  const count = Number(cartData.item_count);

  document
    .querySelectorAll(
      '[data-cart-count], [data-header-cart-count], .cart-count-bubble'
    )
    .forEach((node) => {

      const currentText = node.textContent.trim();

      /* Keep Futurer Header's (0) → (1) formatting */
      if (/^\(\d+\)$/.test(currentText)) {
        node.textContent = `(${count})`;
      } else {
        node.textContent = String(count);
      }

      node.hidden = false;
    });
};
    const emitCartEvents = (lineItem, cartData, source) => {
      const detail = { source: 'cloud-x', cta_source: source, line_item: lineItem, cart: cartData };
      ['cart:updated', 'cart:change', 'theme:cart:change', 'product:added'].forEach((eventName) => {
        document.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
      });
      document.body?.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail, bubbles: true }));
      window.dispatchEvent(new CustomEvent('cloud-x:cart-updated', { detail }));
    };
    const syncThemeCart = async (lineItem, source) => {
      cartSurfaces().forEach((surface) => {
        if (lineItem.sections && typeof surface.renderContents === 'function') {
          try { surface.renderContents(lineItem); } catch { /* Continue with generic events. */ }
        }
      });

      let cartData = null;
      try {
        const cartResponse = await fetch(cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          signal: abortController.signal
        });
        if (cartResponse.ok) cartData = await cartResponse.json();
      } catch { /* The successful add response remains authoritative. */ }

      updateCartCount(cartData);
      emitCartEvents(lineItem, cartData, source);

      if (window.xoStore?.set && cartData) {
        try {
          const commit = window.xoStore.set('xo-cart', (previous) => ({
            ...previous,
            size: cartData.item_count,
            sections: lineItem.sections || previous.sections,
            item: lineItem,
            isAdded: true,
            addErrorMessage: '',
            variantId: String(lineItem.variant_id || lineItem.id || '')
          }));
          if (typeof commit === 'function') commit('cloud-x/addCartSuccess');
        } catch { /* Gearer bridge is an enhancement, never a dependency. */ }
      }

      const externalAdapter = window.CloudXShopifyAdapter;
      if (externalAdapter && typeof externalAdapter.afterAdd === 'function') {
        try { await externalAdapter.afterAdd({ root, lineItem, cart: cartData, source }); } catch { /* Keep checkout usable. */ }
      }

      if (cartBehavior === 'page') {
        window.location.assign(cartUrl);
        return;
      }
      if (cartBehavior === 'stay') return;

      /* =========================================
   Open Futurer / XO cart drawer
   ========================================= */




/* =========================================
   Open Futurer / XO Cart Drawer
   ========================================= */

const xoCartModal =
  document.querySelector(
    'xo-modal[xo-name="cart"][xo-for-cart-mini]'
  );

if (xoCartModal) {

  /* Refresh Cart Mini with the HTML returned by
     Shopify's bundled section rendering */
  const cartSectionHtml =
    lineItem?.sections?.['xo-cart-mini'];

  const currentCartMini =
    document.querySelector(
      'xo-cart-mini#xo-cart-mini'
    );

  if (cartSectionHtml && currentCartMini) {
    try {
      const parsed =
        new DOMParser().parseFromString(
          cartSectionHtml,
          'text/html'
        );

      const freshCartMini =
        parsed.querySelector(
          'xo-cart-mini#xo-cart-mini'
        );

      if (freshCartMini) {
        currentCartMini.innerHTML =
          freshCartMini.innerHTML;
      }
    } catch (error) {
      console.warn(
        'Cloud X: Cart Mini refresh failed.',
        error
      );
    }
  }


  /* Use XO's own modal system.
     This is equivalent to the native
     <xo-modal-trigger xo-name="cart"> behavior. */
  const xoCartTrigger =
    document.createElement('xo-modal-trigger');

  xoCartTrigger.setAttribute(
    'xo-name',
    'cart'
  );

  xoCartTrigger.setAttribute(
    'role',
    'button'
  );

  xoCartTrigger.setAttribute(
    'tabindex',
    '-1'
  );

  xoCartTrigger.style.position = 'fixed';
  xoCartTrigger.style.left = '-9999px';
  xoCartTrigger.style.top = '-9999px';

  document.body.appendChild(
    xoCartTrigger
  );

  requestAnimationFrame(() => {
    xoCartTrigger.click();

    window.setTimeout(() => {
      xoCartTrigger.remove();
    }, 1000);
  });

  return;
}


/* Fallback for non-XO themes */
const surface = cartSurfaces()[0];

if (surface) {

  if (typeof surface.open === 'function') {
    surface.open();

  } else if (
    surface.tagName.toLowerCase() === 'details'
  ) {
    surface.open = true;

  }
}
    };
    const addToCart = async (cartForm, source, eventData = {}) => {
  const submitButton = cartForm.querySelector('[type="submit"]');
  const originalDisabled = submitButton?.disabled;

  const stickyAddButton =
    one('[data-cloud-x-sticky-add]');

  const stickyOriginalDisabled =
    stickyAddButton?.disabled;

  const isStickyAdd =
    source === 'sticky';

  setCartError('');

  if (
    isStickyAdd &&
    stickyAddButton
  ) {
    stickyAddButton.disabled = true;

    stickyAddButton.setAttribute(
      'aria-busy',
      'true'
    );
  }
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
      }
      cartForm.classList.add('is-loading');

      try {
        const formData = new FormData(cartForm);
        if (!formData.get('quantity')) formData.set('quantity', '1');
        const sections = cartSectionIds();
        if (sections.length) formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
          signal: abortController.signal
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status || result.errors) {
          throw new Error(result.description || result.message || (typeof result.errors === 'string' ? result.errors : '') || i18n.add_error || 'Unable to add this item to the cart.');
        }
        await syncThemeCart(result, source);
        emitAnalytics('add_to_cart', eventData);
        showToast(i18n.added || 'Added to cart.');
        return result;
      } catch (error) {
        const errorText = error?.message || i18n.add_error || 'Unable to add this item to the cart.';
        setCartError(errorText);
        showToast(errorText);
        emitAnalytics('add_to_cart_error', { ...eventData, error: errorText });
        return null;
      } finally {
  cartForm.classList.remove('is-loading');

  if (submitButton) {
    submitButton.disabled = Boolean(originalDisabled);
    submitButton.removeAttribute('aria-busy');
  }

  if (
    isStickyAdd &&
    stickyAddButton
  ) {
    stickyAddButton.disabled =
      Boolean(stickyOriginalDisabled);

    stickyAddButton.removeAttribute(
      'aria-busy'
    );
  }

  if (cartForm === form) {
    updateVariant();
  }
}
    };
    /* =========================================
       Cloud X purchase controls
       Hero + Sticky share one real purchase flow
       ========================================= */

    const getCloudXPurchaseState = () => {
      const selected = variants.find(
        (item) =>
          String(item.id) === String(variantInput?.value)
      );

      const quantity = clampQuantity(
        stickyQuantityInput?.value ||
        quantityInput?.value ||
        1
      );

      setQuantity(quantity);

      return {
        selected,
        quantity
      };
    };


    const cloudXAddToCart = (source = 'hero') => {
      if (!form) return;

      const { selected, quantity } =
        getCloudXPurchaseState();

      if (!selected || !selected.available) {
        setCartError(
          i18n.unavailable ||
          'This option is currently unavailable.'
        );
        return;
      }

      pendingCtaSource = source;

      if (quantityInput) {
        quantityInput.value = String(quantity);
      }

      const eventData = {
        source,
        variant_id: selected.id,
        variant_title: selected.title,
        value: selected.price / 100,
        currency: root.dataset.currency
      };

      emitAnalytics('add_to_cart_click', eventData);

      showToast(
        message('adding', { title: selected.title }) ||
        `Adding ${selected.title}…`
      );

      addToCart(
        form,
        source,
        eventData
      );

      pendingCtaSource = 'hero';
    };


    const cloudXBuyNow = () => {
      const { selected, quantity } =
        getCloudXPurchaseState();

      if (!selected || !selected.available) {
        setCartError(
          i18n.unavailable ||
          'This option is currently unavailable.'
        );
        return;
      }

      emitAnalytics('buy_now_click', {
        source: 'buy_now',
        variant_id: selected.id,
        variant_title: selected.title,
        value: selected.price / 100,
        currency: root.dataset.currency
      });

      const shopRoot =
        window.Shopify?.routes?.root || '/';

      const normalizedRoot =
        shopRoot.endsWith('/')
          ? shopRoot
          : `${shopRoot}/`;

      window.location.assign(
        `${normalizedRoot}cart/${selected.id}:${quantity}`
      );
    };



listen(
  one('[data-cloud-x-sticky-add]'),
  'click',
  (event) => {
    event.preventDefault();

    const heroAddButton =
      one('.cloud-x-product-form .btn-primary');

    const quantity =
      clampQuantity(
        stickyQuantityInput?.value ||
        quantityInput?.value ||
        1
      );

    setQuantity(quantity);

    if (
      form &&
      heroAddButton &&
      !heroAddButton.disabled
    ) {
      form.requestSubmit(heroAddButton);
    }
  }
);


    /* STICKY — Buy it now */
    listen(
      one('[data-cloud-x-sticky-buy-now]'),
      'click',
      (event) => {
        event.preventDefault();

        cloudXBuyNow();
      }
    );


    /* HERO — Buy it now
       Capture the click before Shopify's
       accelerated-payment wrapper handles it.
    */
    const heroPaymentContainer =
      one('[data-cloud-x-payment-button]');

    listen(
      heroPaymentContainer,
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        cloudXBuyNow();
      },
      { capture: true }
    );
    all('.cloud-x-product-form, .cloud-x-cross-sell-form').forEach((cartForm) => {
      listen(cartForm, 'submit', (event) => {
        const isCheckout = event.submitter?.name === 'checkout';
        const isMainForm = cartForm === form;
        const selected = isMainForm
          ? variants.find((item) => String(item.id) === String(variantInput?.value))
          : null;
        if (isCheckout) {
          if (selected?.available) emitAnalytics('buy_now_click', {
            source: 'buy_now',
            variant_id: selected.id,
            variant_title: selected.title,
            value: selected.price / 100,
            currency: root.dataset.currency
          });
          return;
        }

        event.preventDefault();
        if (isMainForm && !selected?.available) return;
        const crossSellButton = cartForm.querySelector('[data-cloud-x-cross-sell-add]');
        const source = isMainForm ? pendingCtaSource : 'cross_sell';
        const eventData = isMainForm ? {
          source,
          variant_id: selected.id,
          variant_title: selected.title,
          value: selected.price / 100,
          currency: root.dataset.currency
        } : {
          source,
          product_id: crossSellButton?.dataset.productId,
          variant_id: crossSellButton?.dataset.variantId
        };
        emitAnalytics(isMainForm ? 'add_to_cart_click' : 'cross_sell_add_click', eventData);
        if (isMainForm) showToast(message('adding', { title: selected.title }) || `Adding Cloud X for ${selected.title}…`);
        addToCart(cartForm, source, eventData);
        pendingCtaSource = 'hero';
      });
    });
    all('[data-cloud-x-product-link]').forEach((link) => listen(link, 'click', () => {
      emitAnalytics('product_click', { source: link.dataset.cloudXProductLink, url: link.href });
    }));
    all('[data-cloud-x-ugc-card] video').forEach((video) => listen(video, 'play', () => {
      emitAnalytics('ugc_play', { card: video.closest('[data-cloud-x-ugc-card]')?.dataset.cloudXUgcCard });
    }, { once: true }));
    all('[data-cloud-x-ugc-link]').forEach((link) => listen(link, 'click', () => {
      emitAnalytics('ugc_creator_click', { card: link.dataset.cloudXUgcLink, url: link.href });
    }));
    listen(one('[data-cloud-x-review-link]'), 'click', () => emitAnalytics('review_click'));

    const recommendationSection = one('[data-cloud-x-recommendations]');
    const recommendationList = recommendationSection?.querySelector('[data-cloud-x-recommendation-list]');
    const recommendationRoot = moduleFor(recommendationSection);
    const formatMoney = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: recommendationRoot.dataset.currency || root.dataset.currency
        }).format(Number(cents || 0) / 100);
      } catch {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${recommendationRoot.dataset.currency || root.dataset.currency}`;
      }
    };
    const renderRecommendation = (productData) => {
      const article = document.createElement('article');
      article.className = 'cloud-x-product-card';
      const imageLink = document.createElement('a');
      imageLink.className = 'cloud-x-product-image';
      imageLink.href = productData.url;
      const imageValue = productData.featured_image || productData.images?.[0];
      const imageUrl = typeof imageValue === 'string' ? imageValue : imageValue?.url;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.loading = 'lazy';
        image.alt = productData.title || '';
        imageLink.append(image);
        if (recommendationRoot.dataset.cloudXShowSecondaryImage === 'true' && productData.images?.[1]) {
          const secondaryValue = productData.images[1];
          const secondaryImage = document.createElement('img');
          secondaryImage.src = typeof secondaryValue === 'string' ? secondaryValue : secondaryValue?.url;
          secondaryImage.loading = 'lazy';
          secondaryImage.alt = productData.title || '';
          secondaryImage.className = 'cloud-x-secondary-image';
          imageLink.append(secondaryImage);
        }
      }
      const body = document.createElement('div');
      body.className = 'cloud-x-product-card-body';
      const heading = document.createElement('h3');
      const titleLink = document.createElement('a');
      titleLink.href = productData.url;
      titleLink.textContent = productData.title || '';
      heading.append(titleLink);
      const price = document.createElement('div');
      price.className = 'cloud-x-product-card-price';
      price.textContent = formatMoney(productData.price);
      const productLink = document.createElement('a');
      productLink.className = 'btn-secondary cloud-x-product-view';
      productLink.href = productData.url;
      productLink.textContent =
  recommendationSection?.dataset.cloudXQuickShopLabel ||
  i18n.choose_options ||
  'View product';
      body.append(heading);
      if (recommendationRoot.dataset.cloudXShowPrice !== 'false') body.append(price);
      body.append(productLink);
      article.append(imageLink, body);
      article.querySelectorAll('a').forEach((link) => listen(link, 'click', () => {
        emitAnalytics('recommendation_click', { product_id: productData.id, url: link.href });
      }));
      return article;
    };
        if (recommendationSection && recommendationList) {
      try {
        const feed = recommendationSection.querySelector(
          '[data-chesona-manual-products]'
        );

        if (!feed) {
          throw new Error('Manual recommendation data is missing');
        }

        const data = JSON.parse(feed.textContent);

        const currentProductId = String(
          feed.dataset.currentProductId || '0'
        );

        const limit = Math.max(
          1,
          Number(
            recommendationSection.dataset.cloudXRecommendationLimit
          ) || 4
        );

        const products = (Array.isArray(data) ? data : [])
          .filter((item) =>
            String(item.id) !== currentProductId &&
            item.available
          )
          .slice(0, limit);

        recommendationList.replaceChildren(
          ...products.map(renderRecommendation)
        );

        recommendationSection.hidden = products.length === 0;
      } catch {
        recommendationSection.hidden = true;
      }
    }

    const selectedColor = one('.cloud-x-color-select')?.value;
    if (selectedColor) setHeroColor(selectedColor);
    updateVariant();
    const initialVariant = variants.find((item) => String(item.id) === String(variantInput?.value));
    emitAnalytics('view_item', {
      variant_id: initialVariant?.id,
      value: initialVariant ? initialVariant.price / 100 : undefined,
      currency: root.dataset.currency
    });

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      abortController.abort();
      observers.forEach((observer) => observer.disconnect());
      window.clearTimeout(toastTimer);
      window.cancelAnimationFrame(headerFrame);
      timers.forEach((timer) => window.clearInterval(timer));
      mediaItems.forEach((item) => item.querySelectorAll('video').forEach((video) => video.pause()));
      sticky?.classList.remove('show');
      document.body?.classList.remove('cloud-x-sticky-visible');
      all('[data-cloud-x-generated]').forEach((node) => node.remove());
      horizontalScrollRegions.forEach((region) => delete region.dataset.cloudXControlsInstalled);
      roots.forEach((moduleRoot) => {
        moduleRoot.classList.remove('sticky-visible');
        delete moduleRoot.dataset.cloudXInitialized;
        delete moduleRoot._cloudXCleanup;
      });
      controllers.delete(controllerKey);
    };
    roots.forEach((moduleRoot) => { moduleRoot._cloudXCleanup = cleanup; });
    controllers.set(controllerKey, { cleanup, roots });
  }

  const rootsIn = (scope = document) => {
    const roots = [...scope.querySelectorAll('.cloud-x-pdp')];
    if (scope.matches?.('.cloud-x-pdp')) roots.unshift(scope);
    return [...new Set(roots)];
  };
  const initAll = (scope = document) => rootsIn(scope).forEach(init);
  const refreshController = (controllerKey) => {
    controllers.get(controllerKey)?.cleanup();
    window.requestAnimationFrame(() => {
      const nextRoot = rootsForController(controllerKey)[0];
      if (nextRoot) init(nextRoot);
    });
  };
  window.CloudXModularSections = {
    version: '4.8.6',
    refresh: () => {
      [...controllers.values()].forEach((controller) => controller.cleanup());
      window.requestAnimationFrame(() => initAll());
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  if (!window.__cloudXThemeEditorHandlers) {
    window.__cloudXThemeEditorHandlers = true;
    document.addEventListener('shopify:section:load', (event) => {
      const loadedRoots = rootsIn(event.target);
      const controllerKeys = new Set(loadedRoots.map(controllerKeyForRoot));
      controllerKeys.forEach(refreshController);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      const unloadedRoots = rootsIn(event.target);
      const controllerKeys = new Set(unloadedRoots.map(controllerKeyForRoot));
      controllerKeys.forEach(refreshController);
    });
  }
})();

/* Bundled Cloud X V4.8.8 UGC stories fallback. */
(() => {
  if (window.__cloudXUgcStoriesLoaded) return;
  window.__cloudXUgcStoriesLoaded = true;

  const modulo = (number, length) => (number + length) % length;
  const videoFromCard = (card) => card?.querySelector('video');
  const coverFromCard = (card) =>
    card?.querySelector('video')?.poster ||
    card?.querySelector('img')?.currentSrc ||
    card?.querySelector('img')?.src ||
    '';
  const playIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7Z"/></svg>';
  const pauseIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zm6 0h4v14h-4z"/></svg>';
  const minimizeIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="12" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.8"/><rect x="9" y="10" width="11" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';
  const volumeIcon = (muted) => muted
    ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="m17 9 4 4m0-4-4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>'
    : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M16 9.5a4 4 0 0 1 0 5m2-8a8 8 0 0 1 0 11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

  const init = (root) => {
    if (!root || root.dataset.cloudXUgcStoriesReady === 'true') return;
    root.dataset.cloudXUgcStoriesReady = 'true';

    const track = root.querySelector('[data-cloud-x-ugc-track]');
    const cards = [...(track?.querySelectorAll('[data-cloud-x-ugc-card]') || [])];
    const viewerCards = cards.filter((card) => videoFromCard(card) || card.querySelector('[data-cloud-x-ugc-external-template]'));
    const viewer = root.querySelector('[data-cloud-x-ugc-viewer]');
    const media = viewer?.querySelector('[data-cloud-x-ugc-viewer-media]');
    const title = viewer?.querySelector('[data-cloud-x-ugc-viewer-title]');
    const close = viewer?.querySelector('[data-cloud-x-ugc-close]');
    const minimize = viewer?.querySelector('[data-cloud-x-ugc-minimize]');
    const dismiss = viewer?.querySelector('[data-cloud-x-ugc-dismiss]');
    const viewerPrev = viewer?.querySelector('[data-cloud-x-ugc-viewer-prev]');
    const viewerNext = viewer?.querySelector('[data-cloud-x-ugc-viewer-next]');
    const sides = [...(viewer?.querySelectorAll('[data-cloud-x-ugc-side]') || [])];
    const center = viewer?.querySelector('[data-cloud-x-ugc-toggle]');
    const mute = viewer?.querySelector('[data-cloud-x-ugc-mute]');
    const share = viewer?.querySelector('[data-cloud-x-ugc-share]');
    const shareSheet = viewer?.querySelector('[data-cloud-x-ugc-share-sheet]');
    const shareClose = viewer?.querySelector('[data-cloud-x-ugc-share-close]');
    const progress = viewer?.querySelector('[data-cloud-x-ugc-progress]');
    const productCard = viewer?.querySelector('.cloud-x-ugc-product');
    const mini = root.querySelector('[data-cloud-x-ugc-miniplayer]');
    const miniMedia = mini?.querySelector('[data-cloud-x-ugc-miniplayer-media]');
    const miniToggle = mini?.querySelector('[data-cloud-x-ugc-miniplayer-toggle]');
    const miniClose = mini?.querySelector('[data-cloud-x-ugc-miniplayer-close]');

    if (!track || !cards.length || !viewer || !media || !mini || !miniMedia) {
      delete root.dataset.cloudXUgcStoriesReady;
      return;
    }

    const owner = root.dataset.cloudXSection || root.dataset.cloudXUgcSection || '';
    viewer.dataset.cloudXUgcPortaled = 'true';
    mini.dataset.cloudXUgcPortaled = 'true';
    if (owner) {
      viewer.dataset.cloudXUgcOwner = owner;
      mini.dataset.cloudXUgcOwner = owner;
    }
    document.body.append(viewer, mini);

    if (center) center.innerHTML = playIcon;
    minimize && (minimize.innerHTML = minimizeIcon);

    let viewerIndex = 0;
    let player = null;
    let miniPlayer = null;
    let raf = 0;
    let lastFocus = null;
    const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const previewRatios = new Map();

    const updateTrackArrows = () => {
      const previous = root.querySelector('[data-cloud-x-ugc-track-prev]');
      const next = root.querySelector('[data-cloud-x-ugc-track-next]');
      if (previous) previous.hidden = track.scrollLeft < 4;
      if (next) next.hidden = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    const moveTrack = (direction) => track.scrollBy({
      left: direction * (cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap || 0)),
      behavior: 'smooth'
    });

    root.querySelector('[data-cloud-x-ugc-track-prev]')?.addEventListener('click', () => moveTrack(-1));
    root.querySelector('[data-cloud-x-ugc-track-next]')?.addEventListener('click', () => moveTrack(1));
    track.addEventListener('scroll', updateTrackArrows, { passive: true });
    updateTrackArrows();

    const pausePreview = (card) => {
      const video = videoFromCard(card);
      video?.pause?.();
      card?.classList.remove('is-preview-playing');
    };
    const playPreview = (card) => {
      const video = videoFromCard(card);
      if (!video) return;
      cards.forEach((candidate) => {
        if (candidate !== card) pausePreview(candidate);
      });
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.play()
        .then(() => card.classList.add('is-preview-playing'))
        .catch(() => card.classList.remove('is-preview-playing'));
    };
    const syncMobilePreview = () => {
      if (fineHover.matches) return;
      let current = null;
      let ratio = 0;
      cards.forEach((card) => {
        const value = previewRatios.get(card) || 0;
        if (value > ratio) {
          ratio = value;
          current = card;
        }
      });
      if (current && ratio >= 0.55) playPreview(current);
      else cards.forEach(pausePreview);
    };

    const renderMute = () => {
      if (!mute) return;
      const muted = player?.tagName === 'VIDEO' ? player.muted : true;
      mute.innerHTML = volumeIcon(muted);
      mute.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      mute.setAttribute('aria-pressed', String(!muted));
    };
    const syncViewerState = () => {
      const isVideo = player?.tagName === 'VIDEO';
      const isPlaying = Boolean(isVideo && !player.paused);
      media.classList.toggle('is-playing', isPlaying);
      media.classList.toggle('is-paused', Boolean(isVideo && player.paused));
      if (center) {
        center.hidden = !isVideo;
        center.innerHTML = playIcon;
        center.setAttribute('aria-label', 'Play video');
      }
      if (mute) mute.hidden = !isVideo;
      renderMute();
    };
    const updateProgress = () => {
      if (player?.tagName === 'VIDEO' && Number.isFinite(player.duration) && player.duration > 0 && progress) {
        progress.value = Math.round(player.currentTime / player.duration * 1000);
      }
      if (player?.tagName === 'VIDEO' && !viewer.hidden) raf = requestAnimationFrame(updateProgress);
    };
    const stopPlayer = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      player?.pause?.();
      player?.remove();
      player = null;
      media.querySelector('iframe')?.remove();
      media.classList.remove('is-playing', 'is-paused');
      if (progress) progress.value = 0;
    };
    const clearMini = () => {
      miniPlayer?.pause?.();
      miniPlayer?.remove();
      miniPlayer = null;
      mini.hidden = true;
      if (miniToggle) miniToggle.innerHTML = pauseIcon;
    };
    const renderMiniState = () => {
      if (!miniToggle || !miniPlayer) return;
      const paused = miniPlayer.paused;
      miniToggle.innerHTML = paused ? playIcon : pauseIcon;
      miniToggle.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    };
    const makePlayer = (card) => {
      const source = videoFromCard(card);
      if (source) {
        const clone = source.cloneNode(true);
        clone.removeAttribute('controls');
        clone.muted = false;
        clone.loop = true;
        clone.playsInline = true;
        clone.preload = 'auto';
        const restoreTime = () => {
          if (Number.isFinite(source.currentTime)) clone.currentTime = source.currentTime;
        };
        if (clone.readyState >= 1) restoreTime();
        else clone.addEventListener('loadedmetadata', restoreTime, { once: true });
        return clone;
      }
      const template = card.querySelector('[data-cloud-x-ugc-external-template]');
      if (template) {
        const fragment = template.content.cloneNode(true);
        const frame = fragment.querySelector('iframe');
        if (frame) return frame;
      }
      return null;
    };
    const refreshSides = () => {
      sides.forEach((side) => {
        const offset = Number(side.dataset.cloudXUgcOffset || 0);
        const target = viewerCards.length > 1
          ? viewerCards[modulo(viewerIndex + offset, viewerCards.length)]
          : null;
        const cover = coverFromCard(target);
        side.style.backgroundImage = cover ? `url("${cover}")` : 'none';
        side.hidden = !target || target === viewerCards[viewerIndex] || !cover;
      });
    };
    const togglePlayer = () => {
      if (player?.tagName !== 'VIDEO') return;
      if (player.paused) player.play().catch(syncViewerState);
      else player.pause();
    };
    const openViewerAt = (nextIndex) => {
      if (!viewerCards.length) return;
      const wasHidden = viewer.hidden;
      clearMini();
      viewerIndex = modulo(nextIndex, viewerCards.length);
      const card = viewerCards[viewerIndex];
      cards.forEach(pausePreview);
      stopPlayer();
      if (title) title.textContent = card.querySelector('.cloud-x-ugc-card-title')?.textContent?.trim() || '';
      player = makePlayer(card);
      if (player) {
        media.prepend(player);
        if (player.tagName === 'VIDEO') {
          player.addEventListener('play', syncViewerState);
          player.addEventListener('pause', syncViewerState);
          player.addEventListener('volumechange', renderMute);
        }
      }
      refreshSides();
      viewer.hidden = false;
      viewer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cloud-x-ugc-viewer-open');
      if (wasHidden) {
        lastFocus = document.activeElement;
        close?.focus?.({ preventScroll: true });
      }
      syncViewerState();
      if (player?.tagName === 'VIDEO') {
        player.play().catch(syncViewerState);
        updateProgress();
      }
    };
    const openAt = (cardIndex) => {
      const card = cards[modulo(cardIndex, cards.length)];
      const playableIndex = viewerCards.indexOf(card);
      if (playableIndex >= 0) openViewerAt(playableIndex);
    };
    const switchBy = (direction) => openViewerAt(viewerIndex + direction);
    const closeViewer = (mode) => {
      const shouldMini = (mode === 'backdrop' || mode === 'minimize') && player?.tagName === 'VIDEO';
      if (shouldMini) {
        miniPlayer = player;
        player = null;
        miniPlayer.removeEventListener('play', syncViewerState);
        miniPlayer.removeEventListener('pause', syncViewerState);
        miniPlayer.removeEventListener('volumechange', renderMute);
        cancelAnimationFrame(raf);
        raf = 0;
        miniMedia.replaceChildren(miniPlayer);
        miniPlayer.loop = true;
        miniPlayer.muted = true;
        miniPlayer.addEventListener('play', renderMiniState);
        miniPlayer.addEventListener('pause', renderMiniState);
        miniPlayer.play().catch(renderMiniState);
        mini.hidden = false;
        renderMiniState();
      } else {
        stopPlayer();
      }
      viewer.hidden = true;
      viewer.setAttribute('aria-hidden', 'true');
      if (shareSheet) shareSheet.hidden = true;
      document.body.classList.remove('cloud-x-ugc-viewer-open');
      media.classList.remove('is-playing', 'is-paused');
      lastFocus?.focus?.({ preventScroll: true });
      lastFocus = null;
    };

    cards.forEach((card, cardIndex) => {
      const open = () => openAt(cardIndex);
      card.addEventListener('click', open);
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      });
      card.addEventListener('pointerenter', () => {
        if (fineHover.matches) playPreview(card);
      });
      card.addEventListener('pointerleave', () => {
        if (fineHover.matches) pausePreview(card);
      });
    });

    const previewObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => previewRatios.set(entry.target, entry.intersectionRatio));
      syncMobilePreview();
    }, { root: null, threshold: [0, 0.25, 0.55, 0.7, 0.85, 1] });
    cards.forEach((card) => previewObserver.observe(card));

    close?.addEventListener('click', () => closeViewer('close'));
    minimize?.addEventListener('click', () => closeViewer('minimize'));
    dismiss?.addEventListener('click', () => closeViewer('backdrop'));
    viewerPrev?.addEventListener('click', () => switchBy(-1));
    viewerNext?.addEventListener('click', () => switchBy(1));
    sides.forEach((side) => side.addEventListener('click', () => switchBy(Number(side.dataset.cloudXUgcOffset || 0))));
    center?.addEventListener('click', togglePlayer);
    media.addEventListener('click', (event) => {
      if (event.target.closest('button, input, a, xo-product-quick-view-trigger, [role="button"]')) return;
      togglePlayer();
    });
    mute?.addEventListener('click', () => {
      if (player?.tagName !== 'VIDEO') return;
      player.muted = !player.muted;
    });
    progress?.addEventListener('input', () => {
      if (player?.tagName === 'VIDEO' && Number.isFinite(player.duration)) {
        player.currentTime = player.duration * Number(progress.value) / 1000;
      }
    });
    share?.addEventListener('click', () => {
      if (shareSheet) shareSheet.hidden = false;
    });
    shareClose?.addEventListener('click', () => {
      if (shareSheet) shareSheet.hidden = true;
    });
    productCard?.addEventListener('click', () => player?.pause?.());
    miniToggle?.addEventListener('click', () => {
      if (!miniPlayer) return;
      if (miniPlayer.paused) miniPlayer.play().catch(renderMiniState);
      else miniPlayer.pause();
    });
    mini?.addEventListener('click', (event) => {
      if (event.target.closest('button, a, [role="button"]')) return;
      if (!miniPlayer) return;
      if (miniPlayer.paused) miniPlayer.play().catch(renderMiniState);
      else miniPlayer.pause();
    });
    miniClose?.addEventListener('click', clearMini);
    fineHover.addEventListener?.('change', () => {
      cards.forEach(pausePreview);
      syncMobilePreview();
    });

    const keydown = (event) => {
      if (viewer.hidden) return;
      if (event.key === 'Escape') closeViewer('close');
      if (event.key === 'ArrowLeft') switchBy(-1);
      if (event.key === 'ArrowRight') switchBy(1);
      if (event.key === ' ' && event.target === viewer) {
        event.preventDefault();
        togglePlayer();
      }
    };
    document.addEventListener('keydown', keydown);

    root._cloudXUgcCleanup = () => {
      document.removeEventListener('keydown', keydown);
      previewObserver.disconnect();
      cards.forEach(pausePreview);
      stopPlayer();
      clearMini();
      viewer.remove();
      mini.remove();
      document.body.classList.remove('cloud-x-ugc-viewer-open');
      delete root.dataset.cloudXUgcStoriesReady;
    };
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('.cloud-x-module--ugc').forEach(init);
    if (scope.matches?.('.cloud-x-module--ugc')) init(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector?.('.cloud-x-module--ugc')?._cloudXUgcCleanup?.();
  });
})();

// End bundled Cloud X UGC stories fallback.
